import {useMemo} from 'react';

import {
  ProcessExecutionType,
  ProcessFlowchartPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {
  ConfiguratorResultType,
  ProcessFlowchartEditorConfiguratorResult,
  useProcessFlowchartEditorConfigurator,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {FEATURE_OVERRIDES_FOR_EDITOR_MODE} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {DefaultProcessesPlugin} from 'processes/graph-builder-processes/processes-plugin/DefaultProcessesPlugin';

import {ExecutionEditorMode} from './ExecutionFlowchartPaneTypes';

interface UseExecutionEditorConfiguratorResult {
  configuratorResult: ProcessFlowchartEditorConfiguratorResult;
  executionEditorConfigurator: ProcessFlowchartEditorConfigurator | null;
}

function buildExecutionEditorConfigurator(
  base: ProcessFlowchartEditorConfigurator,
  editorMode: ExecutionEditorMode
): ProcessFlowchartEditorConfigurator {
  const features = new Map(base.platformFlowchartFeatures);
  const overrides = FEATURE_OVERRIDES_FOR_EDITOR_MODE[editorMode];
  for (const platformFeature of Object.values(ProcessFlowchartPlatformFeatureType)) {
    const override = overrides[platformFeature];
    if (override != null) {
      features.set(platformFeature, override);
    }
  }
  return new ProcessFlowchartEditorConfigurator(
    base.flowchartTypeName,
    [ProcessExecutionType.FLOWCHART],
    features
  );
}

export function useExecutionEditorConfigurator(
  editorMode: ExecutionEditorMode
): UseExecutionEditorConfiguratorResult {
  const configuratorResult = useProcessFlowchartEditorConfigurator(
    DefaultProcessesPlugin.FLOWCHART_TYPE_NAME
  );
  const baseConfigurator =
    configuratorResult.resultType === ConfiguratorResultType.SUCCESS ? configuratorResult.configurator : null;

  const executionEditorConfigurator = useMemo(
    () => (baseConfigurator != null ? buildExecutionEditorConfigurator(baseConfigurator, editorMode) : null),
    [baseConfigurator, editorMode]
  );

  return {configuratorResult, executionEditorConfigurator};
}
